import { Download, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { ContentContainer, PageLayout, PageTitle } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  useGetPriceRemarkPendingExpiredQuery,
  useGetPriceRemarkPendingNetworkQuery,
} from "@/services/price-remark/pending";
import { useGetStoresFilterableOptionsQuery } from "@/services/store/select-options";
import { PendingStore } from "@/services/price-remark/types";
import { formatNumber } from "@/utils/formatToMoney";

import { KpiCards } from "../../components/kpi-cards";
import { QueryErrorAlert } from "../../components/query-error-alert";
import { extractQueryErrorMessage } from "../../utils/query-error";
import { NetworkTable } from "../components/network-table";
import { PendingFilter } from "../components/pending-filter";
import { WindowBar } from "../components/window-bar";
import { usePendingFilterActions } from "../ducks/filter/hooks/actions";
import { usePendingFilterState } from "../ducks/filter/hooks/filterState";
import { PendingStoreScope } from "../ducks/filter/types";
import { useExportCollectionList } from "../hooks/use-pending-exports";
import { useStableScrollbar } from "../hooks/use-stable-scrollbar";

const SCOPES: Array<{ value: PendingStoreScope; label: string }> = [
  { value: "ACTION", label: "Precisam de ação" },
  { value: "SILENT", label: "Silenciosas" },
  { value: "IDLE", label: "Paradas" },
  { value: "ALL", label: "Todas" },
];

const SCOPE_STATUS: Record<PendingStoreScope, string | null> = {
  ACTION: null,
  SILENT: "SILENCIOSA",
  IDLE: "PARADA",
  ALL: null,
};

function matchesScope(store: PendingStore, scope: PendingStoreScope): boolean {
  if (scope === "ALL") return true;
  if (scope === "ACTION") return store.status !== "ATIVA";
  return store.status === SCOPE_STATUS[scope];
}

export function PriceRemarkPending() {
  useStableScrollbar();

  const navigate = useNavigate();
  const { filters } = usePendingFilterState();
  const { updateFilter } = usePendingFilterActions();
  const { exportAll, isExporting } = useExportCollectionList();

  const { data, isFetching, isError, error, refetch } =
    useGetPriceRemarkPendingNetworkQuery({ labelType: filters.labelType });

  const { data: expired, isFetching: isLoadingExpired } =
    useGetPriceRemarkPendingExpiredQuery({ labelType: filters.labelType });

  const { data: storeOptions } = useGetStoresFilterableOptionsQuery();

  const regionByStore = useMemo(() => {
    const map = new Map<number, string>();
    for (const option of storeOptions ?? []) {
      const label = option.regionalName ?? option.state;
      if (label) map.set(Number(option.value), label);
    }
    return map;
  }, [storeOptions]);

  const regions = useMemo(
    () => [...new Set(regionByStore.values())].sort((a, b) => a.localeCompare(b)),
    [regionByStore],
  );

  const stores = useMemo(() => {
    const term = filters.search.trim().toLowerCase();

    return (data?.stores ?? []).filter((store) => {
      if (!matchesScope(store, filters.scope)) return false;

      if (filters.region && regionByStore.get(store.storeCode) !== filters.region) {
        return false;
      }

      if (!term) return true;
      return (
        String(store.storeCode).includes(term) ||
        (store.city ?? "").toLowerCase().includes(term) ||
        (store.storeName ?? "").toLowerCase().includes(term)
      );
    });
  }, [data, filters.scope, filters.region, filters.search, regionByStore]);

  const kpis = useMemo(() => {
    const summary = data?.summary;
    return [
      {
        label: "Lojas silenciosas",
        value: summary?.silent ?? 0,
        tone: "danger" as const,
        hint: "nenhuma etiqueta na janela",
      },
      {
        label: "Lojas paradas",
        value: summary?.idle ?? 0,
        tone: "danger" as const,
        hint: "emitiram e pararam",
      },
      {
        label: "Lotes não iniciados",
        value: summary?.notStartedBatches ?? 0,
        tone: "warning" as const,
        hint: "ninguém tocou",
      },
      {
        label: "Lojas ativas",
        value: summary?.active ?? 0,
        tone: "positive" as const,
        hint: "nenhum lote intocado",
      },
      {
        label: "Itens a remarcar",
        value: formatNumber(summary?.items ?? 0),
        hint: "com preço novo diferente do vigente",
      },
      {
        label: "Lojas na janela",
        value: summary?.stores ?? 0,
        hint: "com lote a remarcar",
      },
    ];
  }, [data]);

  return (
    <PageLayout>
      <PageTitle
        title="Pendências de remarcação"
        description="Quem ainda não remarcou — rede inteira, ao vivo."
      />
      <ContentContainer>
        <div className="space-y-4">
          <WindowBar
            window={data?.window}
            durationMs={data?.durationMs}
            cache={data?.cache}
            expired={expired}
            isLoadingExpired={isLoadingExpired}
          />

          <div className="flex flex-wrap items-end justify-between gap-3">
            <PendingFilter regions={regions} showSearch />
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={isFetching}
              onClick={() => refetch()}
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Atualizar
            </Button>
          </div>

          {isError ? (
            <QueryErrorAlert
              message={extractQueryErrorMessage(error)}
              onRetry={refetch}
            />
          ) : (
            <>
              <KpiCards items={kpis} />

              <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Lojas</p>
                    <p className="text-xs text-gray-500">
                      {stores.length} de {data?.summary.stores ?? 0} · ordenadas
                      da mais crítica
                    </p>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <Label className="text-xs text-gray-500">Situação:</Label>
                    {SCOPES.map((scope) => (
                      <Button
                        key={scope.value}
                        type="button"
                        variant={
                          filters.scope === scope.value ? "default" : "outline"
                        }
                        size="sm"
                        className="h-7"
                        onClick={() =>
                          updateFilter({ key: "scope", value: scope.value })
                        }
                      >
                        {scope.label}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      className="ml-1 h-8"
                      disabled={isExporting || (data?.stores.length ?? 0) === 0}
                      onClick={() => exportAll({ labelType: filters.labelType })}
                    >
                      <Download className="mr-1 h-4 w-4" />
                      {isExporting ? "Solicitando..." : "Exportar pendências"}
                    </Button>
                  </div>
                </div>

                <NetworkTable
                  stores={stores}
                  isLoading={isFetching}
                  regionByStore={regionByStore}
                  onOpenStore={(storeCode) => navigate(`loja/${storeCode}`)}
                />

                <p className="border-t bg-muted/30 px-4 py-3 text-xs text-gray-500">
                  <strong>Itens a remarcar</strong> é a conta exata: só entram
                  os itens cujo preço novo difere do preço normal vigente hoje.
                  Lote cujo preço já virou corrente sai da lista sozinho — a
                  pendência de ontem pode não existir mais hoje, e isso joga a
                  favor da cobrança.
                </p>
              </Card>
            </>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
}

export default PriceRemarkPending;
